import { trackTranslationDirectedRetranslate } from '@src/analytics/EventTracker';
import Modal from '@src/components/common/Modal';
import { collectUserFixes } from '@src/stores-v2/translationSpanEdit';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import type {
  DirectedRetranslateResult,
  TranslationStore,
} from '@src/stores-v2/TranslationStore';

export interface DirectedRetranslateModalProps {
  /** Target language BCP-47 code whose whole translation is re-translated. */
  language: string;

  /**
   * The translation store. Passed explicitly (rather than read off the hook)
   * so the workspace host owns the wiring and the modal unit-tests against a
   * plain store. Only two members are used: `getTranslation` (to count the
   * current fixes for the Fresh-rebase confirm) and `directedRetranslateWhole`.
   */
  translationStore: TranslationStore;

  /** Whether the modal is mounted. Forwarded to the shared `Modal` shell. */
  isOpen: boolean;

  /** Called when the reviewer cancels, or after a successful run closes. */
  onClose: () => void;
}

/** The reviewer's choice for what happens to existing manual fixes. */
type Mode = 'fresh' | 'keep';

/**
 * DirectedRetranslateModal — PLAT-10258 (Atom 3, epic PLAT-10209, D13).
 *
 * The reviewer-facing modal for a whole-script directed re-translate. The
 * reviewer types a free-text steer and picks one of two paths:
 *
 * - **Fresh rebase** (`keepFixes: false`): re-translate from scratch, discarding
 *   every existing manual fix. When the current translation carries ≥1 fix
 *   (counted by the pure `collectUserFixes` selector off
 *   `getTranslation(language)`), a confirm step NAMES the count so the reviewer
 *   can't silently lose work. A fix-free translation skips the confirm — nothing
 *   is discarded.
 * - **Keep my fixes** (`keepFixes: true`): re-translate, then re-apply each fix
 *   re-anchored on its source indices. Any fix that can no longer re-attach is
 *   returned in `unresolved` and listed WITH ITS TEXT in an ephemeral post-run
 *   summary (never persisted — Denys's decision) so the reviewer can redo it.
 *
 * All orchestration lives in the store method `directedRetranslateWhole`; this
 * component is purely the UI, the confirm gate, and the analytics fire. The
 * modal chrome (shell, close button) is the shared `Modal` used by the sibling
 * `TranslationGenerateModal`; no new modal chrome is hand-rolled.
 */
export const DirectedRetranslateModal: React.FC<
  DirectedRetranslateModalProps
> = ({ language, translationStore, isOpen, onClose }) => {
  const [instruction, setInstruction] = useState('');
  const [mode, setMode] = useState<Mode>('fresh');
  // Non-null ⇒ the Fresh-rebase confirm step is open; the value is the count of
  // fixes that will be discarded (captured at click time so it can't drift).
  const [confirmDiscardCount, setConfirmDiscardCount] = useState<number | null>(
    null,
  );
  // The post-run outcome for the Keep-my-fixes summary. Ephemeral component
  // state only — never persisted (DESIGN "Decisions").
  const [result, setResult] = useState<DirectedRetranslateResult | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The modal is mounted for the workspace's whole lifetime (the shared `Modal`
  // shell only toggles its children on `isOpen`), so this local state would
  // otherwise survive a close → reopening showed a stale instruction, a stale
  // Fresh-rebase confirm step, or a stale unresolved-fix summary. Reset every
  // field when the modal closes so each reopen starts clean. Reset on close
  // (not open) so there is no one-frame flash of stale content.
  useEffect(() => {
    if (!isOpen) {
      setInstruction('');
      setMode('fresh');
      setConfirmDiscardCount(null);
      setResult(null);
      setInFlight(false);
      setError(null);
    }
  }, [isOpen]);

  const canSubmit = instruction.trim().length > 0 && !inFlight;

  /**
   * Count the reviewer's live fixes on the current translation. The single
   * source of truth for the Fresh-rebase discard count — the same pure selector
   * the store snapshots for Keep mode, so the number the confirm names matches
   * exactly what a rebase would drop.
   */
  const countCurrentFixes = (): number => {
    const translation = translationStore.getTranslation(language);
    if (!translation) return 0;
    return collectUserFixes(
      translation.phrases,
      translation.parentPhrases ?? [],
    ).length;
  };

  const runRetranslate = async (keepFixes: boolean, fixCount: number) => {
    setInFlight(true);
    setError(null);
    try {
      const outcome = await translationStore.directedRetranslateWhole(
        language,
        {
          instruction,
          keepFixes,
        },
      );
      // Fire only after the store call RESOLVES successfully (AC 5, F5): a
      // failed/aborted run must not emit a success-shaped event. Minimal
      // payload: which path, the TRIMMED steer length (the submit gate trims,
      // so the payload should too), and how many fixes were live at confirm.
      trackTranslationDirectedRetranslate({
        mode: keepFixes ? 'keep' : 'fresh',
        instruction_length: instruction.trim().length,
        fix_count: fixCount,
      });
      setResult(outcome);
      // Keep mode with un-reattachable fixes holds the modal open to show the
      // summary; every other successful path closes.
      if (keepFixes && outcome.unresolved.length > 0) {
        setInFlight(false);
        setConfirmDiscardCount(null);
        return;
      }
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Re-translate failed. Try again.',
      );
      setInFlight(false);
      setConfirmDiscardCount(null);
    }
  };

  const handlePrimary = () => {
    if (!canSubmit) return;
    if (mode === 'fresh') {
      const fixCount = countCurrentFixes();
      // A fix-free rebase discards nothing — skip the confirm and proceed.
      if (fixCount > 0) {
        setConfirmDiscardCount(fixCount);
        return;
      }
      void runRetranslate(false, 0);
      return;
    }
    // Keep my fixes: count is informational only (the store re-snapshots), but
    // we pass it to analytics so both modes report the same figure.
    void runRetranslate(true, countCurrentFixes());
  };

  const handleConfirmFresh = () => {
    void runRetranslate(false, confirmDiscardCount ?? 0);
  };

  const handleCancel = () => {
    if (inFlight) return;
    onClose();
  };

  const unresolved = result?.unresolved ?? [];
  const showSummary = unresolved.length > 0;
  const isConfirming = confirmDiscardCount !== null;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      disableEscapeClose={inFlight}
    >
      <Wrapper>
        <Content>
          <Title>Directed Re-translate</Title>

          {showSummary ? (
            // Ephemeral post-run summary (Keep mode). Lists each fix that could
            // not re-attach WITH ITS TEXT so the reviewer can redo it by hand.
            <Section>
              <SummaryHeading role="status">
                {unresolved.length} fix{unresolved.length === 1 ? '' : 'es'}{' '}
                could not be re-applied
              </SummaryHeading>
              <SummaryBody>
                These fixes no longer matched the re-translated script. Redo
                them by hand:
              </SummaryBody>
              <SummaryList>
                {unresolved.map((fix, i) => (
                  <SummaryItem key={`${fix.origin}-${i}`}>
                    <SummaryFixText>{fix.translatedValue}</SummaryFixText>
                    <SummaryFixOrigin>{fix.origin}</SummaryFixOrigin>
                  </SummaryItem>
                ))}
              </SummaryList>
            </Section>
          ) : isConfirming ? (
            // Fresh-rebase confirm step — NAMES the discard count (AC 2).
            <Section>
              <ConfirmPanel
                role="alertdialog"
                aria-label="Confirm fresh rebase"
              >
                <ConfirmHeading>
                  Discard {confirmDiscardCount} fix
                  {confirmDiscardCount === 1 ? '' : 'es'}?
                </ConfirmHeading>
                <ConfirmBody>
                  A fresh rebase will discard {confirmDiscardCount} manual fix
                  {confirmDiscardCount === 1 ? '' : 'es'} on this translation
                  and re-translate the whole script from scratch. This cannot be
                  undone.
                </ConfirmBody>
              </ConfirmPanel>
            </Section>
          ) : (
            <>
              <Section>
                <SectionSublabel>Instruction</SectionSublabel>
                <InstructionTextarea
                  aria-label="Re-translate instruction"
                  placeholder="e.g. Use a more formal tone; keep product names in English."
                  value={instruction}
                  disabled={inFlight}
                  onChange={e => setInstruction(e.target.value)}
                />
              </Section>

              <Section>
                <SectionSublabel>Existing manual fixes</SectionSublabel>
                <ModeChoice role="radiogroup" aria-label="Fix handling">
                  <ModeOption>
                    <input
                      type="radio"
                      name="directed-retranslate-mode"
                      value="fresh"
                      checked={mode === 'fresh'}
                      disabled={inFlight}
                      onChange={() => setMode('fresh')}
                    />
                    <ModeText>
                      <ModeTitle>Fresh Rebase</ModeTitle>
                      <ModeDesc>
                        Discard existing manual fixes and re-translate from
                        scratch.
                      </ModeDesc>
                    </ModeText>
                  </ModeOption>
                  <ModeOption>
                    <input
                      type="radio"
                      name="directed-retranslate-mode"
                      value="keep"
                      checked={mode === 'keep'}
                      disabled={inFlight}
                      onChange={() => setMode('keep')}
                    />
                    <ModeText>
                      <ModeTitle>Keep My Fixes</ModeTitle>
                      <ModeDesc>
                        Re-translate, then re-apply your fixes where they still
                        fit. Any that no longer fit are listed so you can redo
                        them.
                      </ModeDesc>
                    </ModeText>
                  </ModeOption>
                </ModeChoice>
              </Section>
            </>
          )}

          {error !== null && <ErrorRow role="alert">{error}</ErrorRow>}
        </Content>

        <Footer>
          {inFlight ? (
            <>
              <GeneratingRow>
                <Spinner />
                <GeneratingLabel>Re-translating…</GeneratingLabel>
              </GeneratingRow>
              <SecondaryBtn type="button" onClick={handleCancel} disabled>
                Cancel
              </SecondaryBtn>
            </>
          ) : showSummary ? (
            <PrimaryBtn type="button" onClick={onClose}>
              Done
            </PrimaryBtn>
          ) : isConfirming ? (
            <>
              <SecondaryBtn
                type="button"
                onClick={() => setConfirmDiscardCount(null)}
              >
                Cancel
              </SecondaryBtn>
              <PrimaryBtn type="button" onClick={handleConfirmFresh}>
                Discard &amp; Retranslate
              </PrimaryBtn>
            </>
          ) : (
            <>
              <SecondaryBtn type="button" onClick={handleCancel}>
                Cancel
              </SecondaryBtn>
              <PrimaryBtn
                type="button"
                onClick={handlePrimary}
                disabled={!canSubmit}
              >
                Retranslate
              </PrimaryBtn>
            </>
          )}
        </Footer>
      </Wrapper>
    </Modal>
  );
};

// --- Styles (mirror TranslationGenerateModal's shell / footer / buttons) ----

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--surface-subtle);
  width: 560px;
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 28px 24px;
`;

const Title = styled.h2`
  margin: 0;
  color: var(--foreground);
  text-align: center;
  font-size: 22px;
  font-weight: 700;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionSublabel = styled.div`
  color: var(--foreground);
  font-size: 12px;
  line-height: 140%;
  font-weight: 600;
  opacity: 0.7;
`;

const InstructionTextarea = styled.textarea`
  min-height: 88px;
  resize: vertical;
  padding: 10px 12px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  color: var(--foreground);
  outline: none;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--primary);
  }
`;

const ModeChoice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModeOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;

  input {
    margin-top: 2px;
    accent-color: var(--primary);
  }
`;

const ModeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModeTitle = styled.div`
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: 600;
`;

const ModeDesc = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.4;
`;

const ConfirmPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  color: var(--warning, #b58105);
  background-color: color-mix(
    in srgb,
    var(--warning, #b58105) 12%,
    transparent
  );
  border: 1px solid var(--warning, #b58105);
  border-radius: 8px;
`;

const ConfirmHeading = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const ConfirmBody = styled.div`
  font-size: 12px;
  line-height: 1.4;
  opacity: 0.9;
`;

const SummaryHeading = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--foreground);
`;

const SummaryBody = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-muted);
`;

const SummaryList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
`;

const SummaryItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const SummaryFixText = styled.span`
  color: var(--foreground);
  font-size: var(--text-sm);
`;

const SummaryFixOrigin = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const ErrorRow = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  color: var(--destructive);
  background-color: color-mix(in srgb, var(--destructive) 12%, transparent);
  border: 1px solid var(--destructive);
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 28px 24px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;
`;

const GeneratingRow = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--foreground);
`;

const GeneratingLabel = styled.span`
  font-size: 14px;
  opacity: 0.85;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: directed-retranslate-spin 0.8s linear infinite;

  @keyframes directed-retranslate-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PrimaryBtn = styled.button`
  flex: 1;
  display: flex;
  height: var(--btn-h-xl, 44px);
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  border-radius: var(--radius-button, 8px);
  border: none;
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-sm, 14px);
  font-weight: var(--font-weight-bold, 700);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryBtn = styled.button`
  flex: 1;
  display: flex;
  height: var(--btn-h-xl, 44px);
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  border-radius: var(--radius-button, 8px);
  border: 1px solid var(--border);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-sm, 14px);
  font-weight: var(--font-weight-bold, 700);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
